/**
 * Page admin du portail étudiant : import paiements + gestion accès.
 */
import React, { useState } from 'react';
import { ImportPaiements } from './ImportPaiements';
import { GestionAcces } from './GestionAcces';
import { HistoriqueImports } from './HistoriqueImports';
import { Parametres } from './Parametres';
import { Reclamations } from './Reclamations';
import { ChatModeration } from './ChatModeration';
import { Actualites } from './Actualites';
import { EtudiantsConnectes } from './EtudiantsConnectes';
import { useGetMaintenanceQuery, useSetMaintenanceMutation } from '../../services/portailAdminApi';
import { useAppSelector } from '../../store/hooks';

const TABS_BASE = [
  { key: 'parametres', label: 'Paramètres', icon: '⚙️' },
  { key: 'connectes', label: 'En ligne', icon: '🟢' },
  { key: 'import', label: 'Import paiements', icon: '📥' },
  { key: 'acces', label: 'Accès étudiants', icon: '🔓' },
  { key: 'reclamations', label: 'Réclamations', icon: '⚠️' },
  { key: 'chat', label: 'Chat étudiants', icon: '💬', superAdminOnly: true },
  { key: 'actualites', label: 'Actualités', icon: '📢' },
  { key: 'historique', label: 'Historique imports', icon: '📜' },
];

export default function PortailAdminPage() {
  const [tab, setTab] = useState<'parametres' | 'connectes' | 'import' | 'acces' | 'reclamations' | 'chat' | 'actualites' | 'historique'>('parametres');
  const { data: maintenance, refetch: refetchMaint } = useGetMaintenanceQuery();
  const [setMaint, { isLoading: settingMaint }] = useSetMaintenanceMutation();
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const TABS = TABS_BASE.filter((t) => !(t as any).superAdminOnly || isSuperAdmin);

  const handleQuickToggle = async () => {
    if (!maintenance?.actif) return;
    if (!window.confirm('Désactiver le mode maintenance ? Le portail redevient accessible aux étudiants.')) return;
    try {
      await setMaint({ actif: false }).unwrap();
      refetchMaint();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {maintenance?.actif && (
        <div className="mb-4 bg-red-600 text-white rounded-lg p-3 flex items-center justify-between shadow-lg animate-pulse">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔧</span>
            <div>
              <div className="font-bold text-sm">PORTAIL EN MAINTENANCE</div>
              <div className="text-xs text-red-100">
                Les étudiants ne peuvent pas se connecter.
                {maintenance.depuis && ` Activé depuis ${new Date(maintenance.depuis).toLocaleString('fr-FR')}`}
                {maintenance.par_nom && ` par ${maintenance.par_nom}`}
                {maintenance.message && ` — Message : « ${maintenance.message} »`}
              </div>
            </div>
          </div>
          <button
            onClick={handleQuickToggle}
            disabled={settingMaint}
            className="px-3 py-1.5 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50 rounded text-sm font-bold whitespace-nowrap"
          >
            {settingMaint ? '...' : '✓ Désactiver'}
          </button>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Portail étudiant — Administration
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Gérez les imports de paiements et les accès des étudiants au portail PWA.
        </p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key as any)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <span className="mr-2">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'parametres' && <Parametres />}
      {tab === 'connectes' && <EtudiantsConnectes />}
      {tab === 'import' && <ImportPaiements />}
      {tab === 'acces' && <GestionAcces />}
      {tab === 'reclamations' && <Reclamations />}
      {tab === 'chat' && isSuperAdmin && <ChatModeration />}
      {tab === 'actualites' && <Actualites />}
      {tab === 'historique' && <HistoriqueImports />}
    </div>
  );
}
